import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import socket from 'src/socket';
import { useAppDispatch, useAppSelector } from 'src/redux';
import isDoNotDisturbPath from 'src/constants/doNotDisturb';
import { ctaProps } from 'src/helpers/ctaTracking';
import { capitalizeText } from 'src/helpers/utils';
import { EXTENSION_STORE_URL } from 'src/helpers/extensionBridge';
import { raiseCorner } from 'src/helpers/floatingCorner';
import { pollAssistantMessage } from 'src/redux/actions/assistant';
import {
  applyAssistantEvent,
  closeAssistant,
  openAssistant,
  setAssistantAudience,
} from 'src/redux/slices/assistant';
import type { AssistantEvent, AssistantNudge } from 'src/redux/types/assistant';
import AiCore from './AiCore';
import {
  POLL_GIVE_UP_MS,
  POLL_INTERVAL_MS,
  SOCKET_GRACE_MS,
} from './constants';
import {
  hasActionBar,
  hasBottomBar,
  isHiddenPath,
  personaFor,
} from './personas';
import { Nudge, OrbButton, OrbDock, OrbLabel, UnreadDot } from './index.styles';
import useOrbPrompt from './useOrbPrompt';

const loadPanel = () => import('./AssistantPanel');
const AssistantPanel = React.lazy(loadPanel);
const CLOSE_ANIMATION_MS = 280;
// Clears Applicant review's action bar, about 108px tall with its margin.
const ACTION_BAR_RAISE = 96;
// What tapping the bubble does, by its link or key; anything else "Show me".
const BUBBLE_ACTIONS: Record<string, string> = {
  extension: 'Add to Chrome',
  daily: 'Plan my day',
  hello: 'Ask me',
};
const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Keeps an in-flight reply moving when its socket pushes are not arriving
 * (socket down, a missed room join): after a quiet spell, read it over HTTP
 * until it finishes.
 */
const useReplyFallback = () => {
  const dispatch = useAppDispatch();
  const live = useAppSelector((state) =>
    state.assistant.messages.find(
      (m) =>
        m.role === 'assistant' &&
        (m.status === 'pending' || m.status === 'streaming'),
    ),
  );
  const liveId = live?.id;
  const liveSeq = live?.seq ?? 0;
  const startedAt = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!liveId) return undefined;
    startedAt.current[liveId] ??= Date.now();
    let poller: number | undefined;
    const quiet = window.setTimeout(() => {
      poller = window.setInterval(() => {
        if (Date.now() - startedAt.current[liveId] > POLL_GIVE_UP_MS) {
          window.clearInterval(poller);
          return;
        }
        dispatch(pollAssistantMessage(liveId));
      }, POLL_INTERVAL_MS);
    }, SOCKET_GRACE_MS);
    return () => {
      window.clearTimeout(quiet);
      window.clearInterval(poller);
    };
  }, [liveId, liveSeq, dispatch]);
};

interface AssistantOrbProps {
  onOpen: () => void;
  hidden: boolean;
  lifted: boolean;
  /** A page not to interrupt (mid-purchase, onboarding): only the dot speaks. */
  muted: boolean;
  /** What the orb said, in its bubble until dismissed or the panel opens. */
  bubble: AssistantNudge | null;
  onBubble: () => void;
  onDismiss: () => void;
  hopping: boolean;
  /** Said something the panel hasn't been opened on yet. */
  unread: boolean;
}

const AssistantOrb = ({
  onOpen,
  hidden,
  lifted,
  muted,
  bubble,
  onBubble,
  onDismiss,
  hopping,
  unread,
}: AssistantOrbProps) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [hover, setHover] = useState(false);

  // Phones never hover, so fetch the panel once the page has settled.
  useEffect(() => {
    const idle = (window as any).requestIdleCallback ?? window.setTimeout;
    idle(() => loadPanel(), { timeout: 4000 });
  }, []);

  const warm = () => {
    setHover(true);
    loadPanel();
  };

  // Over a phone's bottom bar a bubble would cover the page: the dot says it.
  const showBubble = !!bubble && !hidden && !muted && !(isPhone && lifted);

  return (
    <OrbDock lifted={lifted} hidden={hidden}>
      {showBubble ? (
        <Nudge
          role="status"
          onClick={onBubble}
          {...ctaProps('assistant_orb_bubble', bubble.key)}
        >
          {bubble.text}
          <span className="go">
            {BUBBLE_ACTIONS[bubble.link ?? bubble.key] ?? 'Show me'}
            <ArrowForwardRoundedIcon />
          </span>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            aria-label="Dismiss"
            sx={{ position: 'absolute', top: 4, right: 4 }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Nudge>
      ) : (
        <OrbLabel show={hover}>
          Ask Abekus AI
          <kbd>{isMac ? '⌘' : 'Ctrl'} K</kbd>
        </OrbLabel>
      )}
      <OrbButton
        hopping={hopping}
        quiet={unread}
        onClick={onOpen}
        onMouseEnter={warm}
        onMouseLeave={() => setHover(false)}
        onFocus={warm}
        onBlur={() => setHover(false)}
        aria-label={
          unread ? 'Open Abekus AI assistant (new)' : 'Open Abekus AI assistant'
        }
      >
        <AiCore size={isPhone ? 54 : 60} mood={hover ? 'hover' : 'idle'} />
        {unread && <UnreadDot aria-hidden />}
      </OrbButton>
    </OrbDock>
  );
};

/**
 * Abekus AI: a floating orb on every page of the user's side except its focus
 * screens, opening a chat that knows the current page and can act across the
 * platform. Who it serves -- and everything shown that is about them -- is the
 * persona for the user's role now (personas/); a role without one gets no
 * assistant. Mounted once at the router root so a reply keeps streaming -- and
 * the conversation stays put -- as the user moves between pages.
 */
const AiAssistant = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { user, isLoginSuccess } = useAppSelector((state) => state.auth);
  const isOpen = useAppSelector((state) => state.assistant.isOpen);
  const chatCheckoutItemId = useAppSelector(
    (state) => state.assistant.chatCheckoutItemId,
  );
  const [closing, setClosing] = useState(false);

  // The native app has its own navigation shell around this web view.
  const isInWebView = !!(window as any)?.ReactNativeWebView;
  const persona = personaFor(user?.user_type);
  const eligible = isLoginSuccess && !!user?.id && !!persona && !isInWebView;
  const hiddenHere = !!persona && isHiddenPath(persona, pathname);

  useReplyFallback();

  // A login that switches role switches assistant: the other side's chat goes.
  useEffect(() => {
    if (persona) dispatch(setAssistantAudience(persona.audience));
  }, [persona, dispatch]);

  useEffect(() => {
    if (!eligible) return undefined;
    const onEvent = (event: AssistantEvent) =>
      dispatch(applyAssistantEvent(event));
    socket.on('assistant_event', onEvent);
    return () => {
      socket.off('assistant_event', onEvent);
    };
  }, [eligible, dispatch]);

  const open = useCallback(() => dispatch(openAssistant()), [dispatch]);

  const orb = useOrbPrompt({
    userId: user?.id ?? '',
    name: capitalizeText((user?.first_name ?? '').trim() || 'there'),
    greeting: persona?.greeting ?? null,
    enabled: eligible,
    blocked: isOpen || hiddenHere || isDoNotDisturbPath(pathname),
  });
  // Tapping the bubble acts on what it says (its prompt, or its link). The
  // orb itself only opens the chat: what it said may be hours old, or never
  // seen (a phone's bottom bar shows only the dot), and a prompt sent for
  // them would spend a daily message they didn't choose to. Either way it
  // has been answered, so the bubble goes.
  const openFromOrb = (viaBubble: boolean) => {
    const said = viaBubble ? orb.bubble : null;
    orb.dismiss();
    if (said?.link === 'extension') {
      window.open(EXTENSION_STORE_URL, '_blank', 'noopener');
      return;
    }
    dispatch(openAssistant(said?.prompt ? { prompt: said.prompt } : undefined));
  };

  const close = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => {
      dispatch(closeAssistant());
      setClosing(false);
    }, CLOSE_ANIMATION_MS);
  }, [dispatch]);

  useEffect(() => {
    // Not over the checkout a chat card opened: it would trap the panel's focus.
    if (!eligible || hiddenHere || chatCheckoutItemId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isOpen) close();
        else open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [eligible, hiddenHere, chatCheckoutItemId, isOpen, open, close]);

  // Walking into a focus screen puts the panel away rather than over the test.
  useEffect(() => {
    if (hiddenHere && isOpen) dispatch(closeAssistant());
  }, [hiddenHere, isOpen, dispatch]);

  // Over a page's own action bar the corner rises, the support bubble with it.
  const raised =
    eligible && !!persona && !hiddenHere && hasActionBar(persona, pathname);
  useEffect(() => {
    if (!raised) return undefined;
    raiseCorner(ACTION_BAR_RAISE);
    return () => raiseCorner(null);
  }, [raised]);

  if (!eligible || !persona) return null;
  const { Extras } = persona;

  return (
    <Box component="aside">
      {/* Kept mounted on focus screens, idle there, so what it waits on survives. */}
      {Extras && <Extras active={!hiddenHere} />}
      {!hiddenHere && (
        <>
          <AssistantOrb
            onOpen={() => openFromOrb(false)}
            hidden={isOpen}
            lifted={hasBottomBar(persona, pathname)}
            muted={isDoNotDisturbPath(pathname)}
            bubble={orb.bubble}
            onBubble={() => openFromOrb(true)}
            onDismiss={orb.dismiss}
            hopping={orb.hopping}
            unread={!!orb.bubble}
          />
          {isOpen && (
            <Suspense fallback={null}>
              <AssistantPanel
                persona={persona}
                closing={closing}
                onClose={close}
              />
            </Suspense>
          )}
        </>
      )}
    </Box>
  );
};

export default AiAssistant;
